import { bannerTypes } from 'domains/banners/constants/banner.constants'
import {
  type PlacementTypeEnum,
  type PlacementPlatformEnum,
  type PlacementExperimentMethodEnum,
  type HeroBannerPlacementInput,
  type SecondaryBannerPlacementInput,
  type FlyoutPlacementInput,
  PlacementDestinationTypeEnum,
  type ButtonVariantEnum,
} from 'domains/banners/api'
import { type BannerFormData, type FlyoutFormData } from 'domains/banners/types/banner.types'

export const isHeroBanner = (bannerType: PlacementTypeEnum): boolean =>
  bannerType === bannerTypes.HERO_BANNER

export const isSecondaryBanner = (bannerType: PlacementTypeEnum): boolean =>
  bannerType === bannerTypes.SECONDARY_BANNER

export const isFlyout = (bannerType: PlacementTypeEnum): boolean =>
  bannerType === bannerTypes.FLYOUT

export const buildDestinationTarget = (
  formData: BannerFormData | FlyoutFormData,
  field: string
): string => {
  switch (formData[field].type) {
    case PlacementDestinationTypeEnum.DoNotNavigate:
      return '{"record_click_attempt":false}'
    case PlacementDestinationTypeEnum.External:
      return JSON.stringify({
        url: formData[field].external.url?.trim(),
        // eslint-disable-next-line @typescript-eslint/naming-convention
        open_in_new_tab: formData[field].external.open_in_new_tab === 'true',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        open_in_new_window: formData[field].external.open_in_new_window === 'true',
      })
    case PlacementDestinationTypeEnum.Storefront:
      return formData[field].storefront.retailerId.replace(/ /g, '')
    case PlacementDestinationTypeEnum.StoreSelector:
      return formData[field].storeselector.retailerIds.replace(/ /g, '')
    case PlacementDestinationTypeEnum.PickupStoreselector:
      return JSON.stringify({
        // eslint-disable-next-line @typescript-eslint/naming-convention
        show_distance: formData[field].pickupstoreselector.showDistance === 'true',
      })
    default:
      return '{"record_click_attempt":false}' // if no destination is selected, do not navigate
  }
}

// TODO: move map FlyoutDataToFlyoutFormData here from BannerUpdate

export const mapFormDataToFlyoutInput = (formData: FlyoutFormData): FlyoutPlacementInput => {
  // primary button always exists, secondary and link might not
  const primaryDestination = {
    type: formData.primaryDestination.type || PlacementDestinationTypeEnum.DoNotNavigate,
    target: buildDestinationTarget(formData, 'primaryDestination'),
  }

  const secondaryDestination = formData.secondaryButton.label
    ? {
        type: formData.secondaryDestination.type || PlacementDestinationTypeEnum.DoNotNavigate,
        target: buildDestinationTarget(formData, 'secondaryDestination'),
      }
    : null

  const linkDestination = formData.linkButton.label
    ? {
        type: formData.linkDestination.type || PlacementDestinationTypeEnum.DoNotNavigate,
        target: buildDestinationTarget(formData, 'linkDestination'),
      }
    : null

  const tempData = {
    ...formData,
    name: formData.name.trim(),
    enabled: formData.bannerEnabled === 'true',
    rank: +formData.bannerRank,
    experiment: {
      name: formData.experiment.name?.trim(),
      variant: formData.experiment.variant?.trim(),
      accessMethod: formData.experiment.accessMethod as PlacementExperimentMethodEnum,
    },
    platform: (formData.platform as string).split(',') as PlacementPlatformEnum[],

    scheduling: {
      startDate: formData.scheduling.startDate,
      endDate: formData.scheduling.endDate || null,
      timezone: formData.scheduling.timezone,
    },
    images: {
      mobile: formData.images.mobile || null,
      desktop: formData.images.desktop || null,
    },

    locale: formData.locale,

    titleText: {
      text: formData.titleText.text?.trim(),
      color: '#333333',
    },

    descriptionText: {
      text: formData.descriptionText.text?.trim(),
      color: '#333333',
    },

    disclaimerText: {
      text: formData.disclaimerText.text?.trim(),
      color: '#333333',
    },

    primaryButton: {
      label: formData.primaryButton.label?.trim(),
      styleVariant: formData.primaryButton.styleVariant as ButtonVariantEnum,
    },

    secondaryButton: {
      label: formData.secondaryButton.label?.trim(),
      styleVariant: formData.secondaryButton.styleVariant as ButtonVariantEnum,
    },

    linkButton: {
      label: formData.linkButton.label?.trim(),
      styleVariant: formData.linkButton.styleVariant as ButtonVariantEnum,
    },

    primaryDestination: primaryDestination,
    secondaryDestination: secondaryDestination,
    linkDestination: linkDestination,
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { bannerEnabled, bannerRank, ...rest } = tempData
  return rest
}

export const mapFormDataToHeroBannerInput = (
  formData: BannerFormData
): HeroBannerPlacementInput => {
  const tempData = {
    ...formData,
    headingText: {
      text: formData.headingText.text?.trim(),
      color: formData.headingText.color,
    },
    subheadingText: {
      text: formData.subheadingText.text?.trim(),
      color: formData.subheadingText.color,
    },
    button: {
      label: formData.button.label?.trim(),
      color: formData.button.color,
      backgroundColor: formData.button.backgroundColor,
    },
    name: formData.name.trim(),
    enabled: formData.bannerEnabled === 'true',
    rank: +formData.bannerRank,
    experiment: {
      name: formData.experiment.name?.trim(),
      variant: formData.experiment.variant?.trim(),
      accessMethod: formData.experiment.accessMethod as PlacementExperimentMethodEnum,
    },
    platform: (formData.platform as string).split(',') as PlacementPlatformEnum[],
    scheduling: {
      startDate: formData.scheduling.startDate,
      endDate: formData.scheduling.endDate || null,
      timezone: formData.scheduling.timezone,
    },
    images: {
      mobile: formData.images.mobile,
      desktop: formData.images.desktop || null,
    },
    destination: {
      type: formData.destination.type,
      target: buildDestinationTarget(formData, 'destination'),
    },
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { bannerEnabled, bannerRank, ...rest } = tempData
  return rest
}

export const mapFormDataToSecondaryBannerInput = (
  formData: BannerFormData
): SecondaryBannerPlacementInput => {
  const tempData = {
    ...formData,
    headingText: {
      text: formData.headingText.text?.trim(),
      color: formData.headingText.color,
    },
    subheadingText: {
      text: formData.subheadingText.text?.trim(),
      color: formData.subheadingText.color,
    },
    secondaryText: {
      text: formData.secondaryText.text?.trim(),
      color: formData.secondaryText.color,
    },
    button: {
      label: formData.button.label?.trim(),
      color: formData.button.color,
      backgroundColor: formData.button.backgroundColor,
    },
    name: formData.name.trim(),
    enabled: formData.bannerEnabled === 'true',
    rank: +formData.bannerRank,
    experiment: {
      name: formData.experiment.name?.trim(),
      variant: formData.experiment.variant?.trim(),
      accessMethod: formData.experiment.accessMethod as PlacementExperimentMethodEnum,
    },
    platform: (formData.platform as string).split(',') as PlacementPlatformEnum[],
    scheduling: {
      startDate: formData.scheduling.startDate,
      endDate: formData.scheduling.endDate || null,
      timezone: formData.scheduling.timezone,
    },
    images: {
      mobile: formData.images.mobile,
      desktop: formData.images.desktop || null,
    },
    destination: {
      type: formData.destination.type,
      target: buildDestinationTarget(formData, 'destination'),
    },
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { bannerEnabled, bannerRank, ...rest } = tempData
  return rest
}
